/**
 * Filter 配置翻译成 CSS `filter:` 字符串。
 *
 * 参数语义对齐 ai-service compositor.apply_filter:
 * - brightness/contrast/saturation: 1.0 ± n/100,n ∈ [-50, 50]
 * - warmth: 正数偏暖,负数偏冷;CSS 没有直接 warmth filter,用 sepia + hue-rotate 近似
 *   小公司用户 + WYSIWYG 预览容忍 5-10% 像素差异(spec §11)
 *
 * font_size_offset 不在 CSS filter 范围内,由调用方单独应用到 contact 字号。
 */
import type { FilterConfig } from "../../../api/posterService";

const ZERO: Required<Omit<FilterConfig, "font_size_offset">> = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
};

// warmth SOT (#14 下载色差):近似 ai-service compositor._apply_warmth 的
// R += warmth/2, B -= warmth/2 通道位移(warmth ∈ [-50,50] → ±25/255,mild 暖/冷)。
// CSS 无通道位移原语,暖调用 subtle sepia、冷调用 subtle hue-rotate 近似。旧系数
// sepia(warmth/100)(warmth=40 → 0.4)/ hue-rotate(warmth*2)(warmth=-40 → -80deg)
// 远比 compositor 重 → 预览比下载明显偏棕/大偏色。这里压到肉眼接近 compositor。
// 精确像素 parity 受 CSS 限制不可达(spec §11 容忍 5-10%),真值以 compositor 下载为准。
const WARMTH_SEPIA_PER_UNIT = 0.004; // warmth=50 → sepia(0.20)
const WARMTH_HUE_PER_UNIT_DEG = 0.3; // warmth=-50 → hue-rotate(-15deg)

export function filterToCss(filter: FilterConfig | null | undefined): string {
  if (!filter) return "none";
  const f = { ...ZERO, ...filter };
  const parts: string[] = [];
  if (f.brightness !== 0) parts.push(`brightness(${1 + f.brightness / 100})`);
  if (f.contrast !== 0) parts.push(`contrast(${1 + f.contrast / 100})`);
  if (f.saturation !== 0) parts.push(`saturate(${1 + f.saturation / 100})`);
  if (f.warmth !== 0) {
    if (f.warmth > 0) {
      const s = Number((f.warmth * WARMTH_SEPIA_PER_UNIT).toFixed(3));
      parts.push(`sepia(${s})`);
    } else {
      const deg = Math.round(f.warmth * WARMTH_HUE_PER_UNIT_DEG);
      parts.push(`hue-rotate(${deg}deg)`);
    }
  }
  return parts.length === 0 ? "none" : parts.join(" ");
}

export function fontSizeWithOffset(baseSize: number, offset: number): number {
  return Math.max(10, Math.min(80, baseSize + offset));
}
